import { compareVersions } from 'compare-versions';
import { cloneDeep, isArray } from 'lodash';
import queryString from 'query-string';
import { isEmptyEffect } from '@meitu/saas-editor-core/es/utils/layer-kit';
import { traverseTree, traverseTreePreOrder, traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { DEFAULT_DPI, SizeUnitType } from '@meitu/saas-editor-core/es/utils/unit-kit';
import {
    formatColorVal,
    IServerLayerProps,
    ITopStruct,
    LATEST_VERSION,
    parseClientLayersToServer,
} from '@meitu/saas-editor-core/es/version-parse';
import {
    CanvasType,
    IImageLayerProps,
    ILayerProps,
    LayerType,
    StageType,
} from '@meitu/saas-editor-core/es/version-types';
import { DownloadImgType } from '@meitu/saas-editor-ui/es/utils/stage';
import { ServerReferenceLineInfo } from '@/types';
import { isBetaEnv, isDevelopEnv, isPreEnv } from '@/utils/env';
import { getQueryParam } from '@/utils/query';

export interface IParseClientToServerTemplateProps {
    stages?: any[];
    layers?: ILayerProps[];
    stageWidth: number;
    stageHeight: number;
    preview: string;
    name: string;
    noPreview?: boolean;
    quality?: number;
    type?: DownloadImgType;
    showHidden?: boolean;
}

export interface IServerBaseTemplate {
    preview: string;
    name: string;
    version: string;
    templateConf: any;
    editMode: CanvasType;
    extraInfo?: { [key: string]: any };
}

export const getBaseTemplate = (data: {
    preview: string;
    name: string;
    version?: string;
    templateConf: any;
    editMode: CanvasType;
    unit: SizeUnitType;
    resolution: number;
    referenceLine?: ServerReferenceLineInfo;
}): IServerBaseTemplate => {
    const baseTemplate: any = {
        ...data,
        version: data.version || LATEST_VERSION,
    };
    // px 单位不需要上报
    if (baseTemplate.unit === SizeUnitType.px) {
        delete baseTemplate.unit;
    }
    if (baseTemplate.resolution === DEFAULT_DPI) {
        delete baseTemplate.resolution;
    }

    return baseTemplate;
};

/**
 * 判断是否移动端不能打开作图记录
 */
export const hasCrossPlatformLock = (layers: IServerLayerProps[]) => {
    const hasLock = !!layers.find((topLayer) => {
        let hasFilterImageLayer = false;

        traverseTree(topLayer, (child: IServerLayerProps) => {
            if (child.layerType === LayerType.IMAGE_LAYER) {
                const { materialIds } = child;

                if (materialIds && materialIds['filter-material'] && materialIds['filter-material']?.length > 0) {
                    hasFilterImageLayer = true;
                }
            }
        });

        return hasFilterImageLayer;
    });

    return hasLock;
};

const getImageEffectMaterialId = (data: IImageLayerProps) => {
    let id = '1703488244124773';
    if (isDevelopEnv() || isPreEnv()) {
        id = '1703237426954176';
    } else if (isBetaEnv()) {
        id = '1703488244124773';
    }
    if (id === '1703488244124773' && data.effect && data.effect.shadow.length) {
        id = '1754288731506773';
    }
    return id;
};

export const parseClientToServerLayers = (layers: ILayerProps[], is900Version: boolean, topStruct: ITopStruct) => {
    let clientLayers = cloneDeep(layers);

    clientLayers = clientLayers.map((layer: ILayerProps) => {
        if (layer.layerType === LayerType.MAGNIFIER) {
            layer.hidden = false;
            return layer;
        }
        // 转换数据再进行保存，但是不要直接修改原始图层的数据
        if (layer.layerType === LayerType.TEXT_LAYER) {
            // transformTextData(layer);
            return layer;
        } else if (layer.layerType === LayerType.GROUP_LAYER) {
            traverseTree(layer, (child: ILayerProps) => {
                if (child.layerType === LayerType.TEXT_LAYER) {
                    // transformTextData(child);
                }
                if (child.layerType === LayerType.IMAGE_LAYER) {
                    if (!isEmptyEffect(child.data as IImageLayerProps)) {
                        child.materialIds = !child.materialIds || isArray(child.materialIds) ? {} : child.materialIds;
                        (child.materialIds as any)['imageEffect-material'] = [
                            getImageEffectMaterialId(child.data as IImageLayerProps),
                        ];
                    }
                }
            });

            return layer;
        } else if (layer.layerType === LayerType.IMAGE_LAYER) {
            if (!isEmptyEffect(layer.data as IImageLayerProps)) {
                layer.materialIds = !layer.materialIds || isArray(layer.materialIds) ? {} : layer.materialIds;
                (layer.materialIds as any)['imageEffect-material'] = [
                    getImageEffectMaterialId(layer.data as IImageLayerProps),
                ];
            }
            return layer;
        } else {
            return layer;
        }
    });

    /*
     * 格式化颜色值  客户端颜色 HEX 后是8位 ，如果6位默认后两位是FF
     */
    formatColorVal(clientLayers);
    const serverLayers = parseClientLayersToServer(clientLayers, is900Version, false, topStruct);

    traverseTrees(serverLayers.layers, (layer: any) => {
        if (layer.materialIds && layer.materialIds['func-list'] && layer.materialIds['func-list'].length >= 0) {
            layer.materialIds['func-list'] = [];
        }

        if (layer.layerType === LayerType.GROUP_LAYER && layer.isTextMaterial) {
            layer.layerType = LayerType.ADVANCED_TEXT;
        }
    });

    return serverLayers;
};

/**
 * 保存接口保存模式
 */
export enum SAVE_API_TYPE {
    /** 手动保存 */
    User = 1,
    /** 自动保存 */
    Auto = 2,
}

export const stageModelToUserData = (template: any, recordInfo: any, cross_platform_lock = false) => {
    return {
        id: recordInfo.id,

        preview: template.preview,

        formula: JSON.stringify(template),

        material_id: recordInfo.material_id,

        // saved: recordInfo.id ? 1 : 0,

        updated_at: recordInfo.updated_at,

        formula_parse_version: template.version,

        canvas_pages: isArray(template.templateConf) ? template.templateConf.length : 1,

        cross_platform_lock: cross_platform_lock ? 1 : 0,

        threshold: recordInfo.threshold || 0,
    } as any;
};

export const getMinVersion = (stageType: StageType, resultStages: any[], extraInfo: any[]) => {
    let version =
        stageType === StageType.VARIETY || (stageType === StageType.SINGLE && resultStages.length > 1)
            ? '9.0.0'
            : '1.0.0';

    extraInfo.forEach((info) => {
        if (info.editorColor || info.originId || info.threshold || info.prompt) {
            version = '9.0.1';
        }
    });

    return version;
};

export const addStagesExtraInfo = (version: string, resultStages: any[], extraInfo: any[]) => {
    if (compareVersions(version, '9.0.1') < 0) {
        return;
    }

    resultStages.forEach((stage, i) => {
        if (extraInfo[i].editorColor) {
            stage.editorColor = extraInfo[i].editorColor;
        }
        if (extraInfo[i].originId) {
            stage.originId = extraInfo[i].originId;
        }
        if (extraInfo[i].threshold) {
            stage.threshold = extraInfo[i].threshold;
        }

        if (extraInfo[i].prompt) {
            stage.prompt = extraInfo[i].prompt;
        }

        if (extraInfo[i].extraInfo) {
            stage.extraInfo = extraInfo[i].extraInfo;
        }
    });
};

export const divideMainLayer = (layers: ILayerProps[], mainLayer: ILayerProps) => {
    const layersMap = new Map<string, ILayerProps>();
    traverseTrees(layers, (child: ILayerProps) => {
        layersMap.set(String(child.id), child);
    });

    const getTopParentLayer = (item: ILayerProps): ILayerProps => {
        const parentLayer = layersMap.get(item.parentId || '');
        if (!parentLayer) {
            return item;
        }

        return getTopParentLayer(parentLayer);
    };
    const topParentLayer = getTopParentLayer(mainLayer);
    const leftLayers = [] as ILayerProps[];
    const rightLayers = [] as ILayerProps[];

    let isRight = false;
    for (let i = 0; i < layers.length; i++) {
        if (layers[i].id === topParentLayer.id) {
            isRight = true;
            let isRightChild = false;
            traverseTreePreOrder(topParentLayer, (child: ILayerProps) => {
                if (!child.children || child.children.length === 0) {
                    if (child.id === mainLayer.id) {
                        isRightChild = true;
                    } else if (isRightChild) {
                        rightLayers.push(child);
                    } else {
                        leftLayers.push(child);
                    }
                }
            });
        } else if (!isRight) {
            leftLayers.push(layers[i]);
        } else {
            rightLayers.push(layers[i]);
        }
    }
    console.log('leftLayers, rightLayers===', leftLayers, rightLayers);
    return [leftLayers, rightLayers];
};

export const stageModelToDesignData = (
    template: any,
    canvas_type: any,
    preview: string,
    multi_preview: any,
    extraInfo: any,
) => {
    const query = queryString.parse(window.location.search);
    const id = query.id as string;
    if (extraInfo && Object.keys(extraInfo).length > 0) {
        template.extraInfo = extraInfo;
    }
    return {
        id,
        /** 模板名称 */
        default_name: template.name,

        /** 模板配方JSON */
        formula: JSON.stringify(template),

        /** 模板预览图地址JSON */
        preview: JSON.stringify({ url: preview }),

        /** 模板错误信息 */
        error: '',

        /** 是否支持v0版本 */
        support_v0: false,

        formula_parse_version: template.version,

        canvas_type,

        multi_preview: JSON.stringify(multi_preview),

        /** 是否是从供稿平台来的 */
        is_new: query.from === 'ggpt' ? '1' : '0',
    } as any;
};

export const stageModelToTextTemplateData = (
    template: any,
    canvas_type: any,
    preview: string,
    multi_preview: any,
    fileUrl: string,
    extraInfo: any,
) => {
    const id = getQueryParam('id') as string;
    template.width = template.templateConf.width;
    template.height = template.templateConf.height;
    template.layer = template.templateConf.layers[1];
    template.templateConf.layers = undefined;
    template.templateConf = undefined;

    if (extraInfo && Object.keys(extraInfo).length > 0) {
        template.extraInfo = extraInfo;
    }

    return {
        id,
        preview,
        error: '',
        canvas_type,
        multi_preview: [],
        version: template.version,
        name: template.name,
        singleLang: true,
        fileUrl,
        config: JSON.stringify(template),
    };
};
